import { memo, useEffect, useState } from "react";
import { useTranslate } from "helpers";

import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";

import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { TemplateEditor } from "ui/components/TemplateEditor";
import { TemplateTag } from "ui/components/TemplateEditor/types";

import { ContractRentTemplateItemEditCardProps } from "./types";


import { CONTRACT_RENT_CONTENT_TEMPLATE_ITEM_EDIT_FORM_SCHEMA } from "./validation";

import { getContractRentTemplateTagsAPI } from "services/api/settings/documents/api";
import { ContractRentTemplateItemFormValues } from "services/api/settings/documents/types";
import { useHandleTextFieldChange } from "services/forms/hooks";


const ContractRentTemplateItemEditCard: React.FC<
  ContractRentTemplateItemEditCardProps
> = (props) => {
  const { item, onSuccess, onClose } = props;
  const { t } = useTranslate();

  const [formValues, setFormValues] =
    useState<ContractRentTemplateItemFormValues>(item);

  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [tags, setTags] = useState<TemplateTag[]>([]);

  useEffect(() => {
    setFormValues(item);
  }, [item]);

  useEffect(() => {
    fetchTemplateTags();
  }, []);

  const fetchTemplateTags = async () => {
    setIsLoading(true);
    try {
      const results =
        await getContractRentTemplateTagsAPI();
      setTags(results);
    } catch {
      setTags([]);
    }
    setIsLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CONTRACT_RENT_CONTENT_TEMPLATE_ITEM_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) return;

    if (onSuccess)
      onSuccess(formValues);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );


  return (
    <>
      <div className="p-4 flex flex-col ">
        <div className="mb-3">
          <TemplateEditor
            name="title"
            value={formValues.title ?? ""}
            onChange={handleTextFieldChange}
            tags={tags}
            label={t("admin.fleet.vehicle.contract.content.add.title")}
            placeholder={t(
              "admin.fleet.vehicle.contract.content.add.title.placeholder"
            )}
            rows={1}
            error={errors.title}
            required
          />
        </div>
        <div className="mb-3">
          <TemplateEditor
            name="content"
            value={formValues.content ?? ""}
            onChange={handleTextFieldChange}
            tags={tags}
            label={t(
              "admin.fleet.vehicle.contract.content.add.description"
            )}
            placeholder={t(
              "admin.fleet.vehicle.contract.content.add.description.placeholder"
            )}
            rows={20}
            error={errors.content}
            required
          />
        </div>
        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
          <Button variant="outlined" onClick={onClose}>
            {t("admin.close")}
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {t("admin.save")}
          </Button>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};

export default memo(ContractRentTemplateItemEditCard);
